import {
  conformityControlTarget,
  customerRequestControlTarget,
  customerRequestStepRoute,
  customerStudioRecordRoute,
} from "@/features/material-compliance/components/customer-requests/customerStudioAgentTargets"
import { declarationReviewPath } from "@/features/material-compliance/components/declarations/routes"
import { masterDataRecordRoute } from "@/features/material-compliance/components/master-data/masterDataAgentTargets"
import {
  importControlTarget,
  isAtImportStage,
  masterDataImportStepRoute,
} from "@/features/material-compliance/components/master-data/masterDataImportAgentTargets"
import {
  inquiryDetailPath,
  inquiryDraftPath,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/routes"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { FeatureFlagMap } from "@/types/featureFlag"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { getRouteByStatus } from "@/utils/questionnaire/questionnaireStatus"
import { AgentAction } from "../types"
import { AgentTarget, isOnTargetRoute, resolveTarget } from "./agentTargets"

type RouteFlags = Pick<
  FeatureFlagMap,
  "EXCEL_INTELLIGENCE" | "PDF_INTELLIGENCE"
>

const KNOWN_STATUSES: ReadonlySet<string> = new Set(
  Object.values(QuestionnaireStatus),
)

/**
 * The decision the frontend makes for an incoming agent action, given the
 * user's current location. Navigation is always user-confirmed (never
 * automatic), so cross-page targets resolve to a `navigate` plan that the UI
 * renders as a "Take me there →" affordance.
 */
export type ActionPlan =
  | { kind: "pulse"; target: AgentTarget }
  | {
      kind: "navigate"
      route: string
      target: AgentTarget | null
      /**
       * Affordance label when there's no target to borrow one from. Raw,
       * dynamic content this module cannot translate (a backend-supplied
       * navigate label, a file name, a record name) — `labelKey` is preferred
       * and takes precedence when both are set.
       */
      label?: string
      /** Translation key for a static affordance label, preferred over `label`. */
      labelKey?: TranslationKeys
    }
  | { kind: "noop" }
  | { kind: "unknown-target" }

/** Normalize a location to its pathname for comparison against target routes. */
function pathOf(location: string): string {
  return location.split("?")[0]
}

export function planAction(
  action: AgentAction,
  currentPath: string,
  flags?: RouteFlags,
  /**
   * Entities the current page registered (see `AgentPageContext`). Needed for
   * stage-scoped controls, where a pathname cannot answer "is the user already
   * there?" — the ERP import flow serves every file set and every stage from one
   * route. Optional: every other action decides from the pathname alone.
   */
  pageEntities?: Record<string, unknown>,
): ActionPlan {
  const here = pathOf(currentPath)

  if (action.type === "navigate") {
    if (here === action.route) {
      return { kind: "noop" }
    }
    return {
      kind: "navigate",
      route: action.route,
      target: null,
      label: action.label,
    }
  }

  if (action.type === "openQuestionnaire") {
    // An unrecognised status would silently route to the wrong screen; send the
    // user to the history list instead, which always exists.
    if (!KNOWN_STATUSES.has(action.status)) {
      return planAction(
        { type: "highlight", targetKey: "history.list" },
        currentPath,
        flags,
      )
    }
    const route = getRouteByStatus(
      action.questionnaireId,
      action.status as QuestionnaireStatus,
      action.fileName,
      action.hasPositionData,
      undefined,
      flags,
    )
    if (here === pathOf(route)) {
      return { kind: "noop" }
    }
    return {
      kind: "navigate",
      route,
      target: null,
      label: action.fileName || undefined,
    }
  }

  if (action.type === "openInquiryDraft") {
    // Never a noop on the same pathname: the composer route is shared by every
    // inquiry, so "already here" would swallow a request for a different one.
    // The target is what gets pulsed once the composer has rendered.
    return {
      kind: "navigate",
      route: inquiryDraftPath(action.inquiryIds, action.stage),
      target: resolveTarget("inquiry.send-button"),
      labelKey: "AI_CHAT/TARGET_LABEL_INQUIRY_SEND",
    }
  }

  if (action.type === "openInquiry") {
    // Never a noop on the same pathname: the list route is shared by every
    // inquiry, so "already here" would swallow a request for a different one.
    return {
      kind: "navigate",
      route: inquiryDetailPath(action.inquiryId),
      target: null,
      labelKey: "AI_CHAT/TARGET_LABEL_THIS_INQUIRY",
    }
  }

  if (action.type === "openHelpArticle") {
    // The route arrives resolved from the backend, which owns the helpdesk's URL
    // shape, so there is nothing to build here. The article title is the label: it is
    // what the agent just named in its answer, so the card reads as the same thing.
    return {
      kind: "navigate",
      route: action.route,
      target: null,
      label: action.title,
    }
  }

  if (action.type === "openDeclarationReview") {
    const route = declarationReviewPath(action.declarationId, {
      statementId: action.statementId,
    })
    // Never a noop on the same pathname: the review route is shared by every
    // statement of one declaration, so "already here" would swallow a hand-over to
    // a different card. No control is pulsed: the four controls' targets lived on the
    // retired Studio V1 screens, and the studio's own buttons do not carry them yet,
    // so a pulse could only wait out its timeout and report the control as missing.
    return {
      kind: "navigate",
      route,
      target: null,
      labelKey: "AI_CHAT/TARGET_LABEL_THIS_DECLARATION",
    }
  }

  if (action.type === "openCustomerStudioRecord") {
    // Never a noop on the same pathname: the inbox route is shared by every record.
    const route = customerStudioRecordRoute(action.entity, action.recordId)
    return {
      kind: "navigate",
      route,
      // The control is what gets pulsed once the editor's own fetch has rendered
      // it. Only a conformity declaration carries one — a request's controls are
      // on its wizard stages.
      target: action.control
        ? conformityControlTarget(action.control, route)
        : null,
      labelKey:
        action.entity === "customer_request"
          ? "AI_CHAT/TARGET_LABEL_CUSTOMER_REQUEST"
          : "AI_CHAT/TARGET_LABEL_CONFORMITY_DECLARATION",
    }
  }

  if (action.type === "openCustomerRequestStep") {
    // Never a noop on the same pathname: every stage of every request shares the
    // same route shape, so "already here" would swallow a hand-over elsewhere.
    const route = customerRequestStepRoute(action.requestId, action.step)
    return {
      kind: "navigate",
      route,
      target: action.control
        ? customerRequestControlTarget(action.control, route)
        : null,
      labelKey: "AI_CHAT/TARGET_LABEL_THIS_REQUEST",
    }
  }

  if (action.type === "openImportStep") {
    const route = masterDataImportStepRoute(action.fileIds, action.step)
    const target = action.control
      ? importControlTarget(action.control, route)
      : null
    // The pathname cannot decide this: one route serves every file set and every
    // stage, and a user who arrived from a history row has no `?step=` to compare
    // against. So ask the page what it is showing. Being on the addressed stage
    // already means pulse — offering "Take me there" for the screen in front of
    // them is the affordance reading as broken.
    if (isAtImportStage(pageEntities, action.fileIds, action.step)) {
      return target ? { kind: "pulse", target } : { kind: "noop" }
    }
    return {
      kind: "navigate",
      route,
      target,
      labelKey: "AI_CHAT/TARGET_LABEL_IMPORT_FLOW",
    }
  }

  if (action.type === "openMasterDataRecord") {
    // Never a noop, even on the same pathname: the list route is shared by every
    // record, so "already here" would swallow a request to select a different one.
    return {
      kind: "navigate",
      route: masterDataRecordRoute(action.entity, action.recordId),
      target: null,
      label: `${action.entity} record`,
    }
  }

  if (action.type !== "highlight") {
    // Proposals and tool calls are handled upstream, never planned here.
    return { kind: "noop" }
  }

  const target = resolveTarget(action.targetKey)
  if (!target) {
    return { kind: "unknown-target" }
  }
  if (isOnTargetRoute(target, here)) {
    return { kind: "pulse", target }
  }
  return { kind: "navigate", route: target.route, target }
}

export interface HighlightDeps {
  waitForSelector: (selector: string, timeoutMs?: number) => Promise<boolean>
  pulse: (selector: string) => boolean
  postAssistantMessage: (text: string) => void
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string
}

/**
 * Wait for a target element to appear, then pulse it. If it never appears (e.g.
 * the feature is flagged off for this account), post a graceful message instead
 * of leaving a broken/no-op affordance.
 */
export async function highlightTarget(
  target: AgentTarget,
  deps: HighlightDeps,
): Promise<void> {
  const found = await deps.waitForSelector(
    target.selector,
    target.waitTimeoutMs,
  )
  if (found && deps.pulse(target.selector)) {
    return
  }
  const label = target.labelKey
    ? deps.translate(target.labelKey)
    : (target.label ?? deps.translate("AI_CHAT/OPEN_PAGE_DEFAULT_LABEL"))
  deps.postAssistantMessage(
    deps.translate("AI_CHAT/TARGET_NOT_LOCATED", { label }),
  )
}
